import React, { useMemo, useRef, useState } from 'react';
import { ScrollView, View, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Card, HelperText, Text, Button } from 'react-native-paper';

import { Optional, ValidUsernameResponseType } from '@luminate/luminate-ts-sdk';
import {
  Header40,
  SubText,
  AccountRegistration,
  useRegistrationContext,
  Strong,
  LabeledSwitch,
  useManagedState,
  ValidatedComponent,
} from '@luminate/luminate-native-components';

import { useAuth, useThemeContext } from '../../contexts';
import { Dialog } from '../common';
import { SCREEN_NAMES } from '../routes';

export const RegisterScreen = () => {
  const { theme } = useThemeContext();
  const navigation = useNavigation();
  const { isValidUsername, isValidPassword } = useAuth();
  const {
    firstName,
    setFirstName,
    middleName,
    setMiddleName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
    email,
    setEmail,
    password,
    setPassword,
    userName,
    setUserName,
    agreeToTerms,
    setAgreeToTerms,
    reset,
  } = useRegistrationContext();

  const [confirmPassword, onConfirmPasswordChanged] = useManagedState('');
  const [usernameIsEmail, onUsernameIsEmailChanged] = useManagedState<Optional<boolean>>(true);
  const [userNameInUse, setUserNameInUse] = useState(false);
  const [userNameFormatInvalid, setUserNameFormatInvalid] = useState(false);
  const [passwordValid, setPasswordValid] = useState<boolean>(true);

  const usernameIsValid = useMemo(() => {
    return !userNameInUse && !userNameFormatInvalid;
  }, [userNameInUse, userNameFormatInvalid]);

  const registrationRef = useRef<ValidatedComponent>(null);

  const resetForm = () => {
    setUserNameInUse(false);
    setUserNameFormatInvalid(false);
    reset();
  };

  const onSignIn = () => {
    resetForm();
    // @ts-ignore
    navigation.navigate(SCREEN_NAMES.SIGN_IN);
  };

  const showForgotPassword = () => {
    // @ts-ignore
    navigation.navigate(SCREEN_NAMES.FORGOT_PASSWORD);
  };

  const usernameInUseError = () => {
    return (
      <Card style={[styles.errorCard]}>
        <Card.Content>
          <HelperText type={'error'} style={{ color: theme.colors.onError }}>
            Someone has already signed up with this username. Please try another one. Or did you{' '}
            <Strong style={{ textDecorationLine: 'underline' }} onPress={showForgotPassword}>
              forget your password
            </Strong>
            ?
          </HelperText>
          <HelperText type={'error'} style={{ color: theme.colors.onError }}>
            Need help?{' '}
            <Strong style={{ textDecorationLine: 'underline' }} onPress={showContactUs}>
              Contact Us
            </Strong>
            !
          </HelperText>
        </Card.Content>
      </Card>
    );
  };

  const usernameFormatError = () => {
    return (
      <Card style={[styles.errorCard]}>
        <Card.Content>
          <HelperText type={'error'} style={{ color: theme.colors.onError }}>
            The username you have chosen is too short. Please try another one.
          </HelperText>
          <HelperText type={'error'} style={{ color: theme.colors.onError }}>
            Need help?{' '}
            <Strong style={{ textDecorationLine: 'underline' }} onPress={showContactUs}>
              Contact Us
            </Strong>
            !
          </HelperText>
        </Card.Content>
      </Card>
    );
  };

  const onCreateAccount = async () => {
    if ((registrationRef.current?.isValid() ?? false) && agreeToTerms) {
      const usernameValid: ValidUsernameResponseType = await isValidUsername(userName as string);
      const passwordValidResponse = await isValidPassword(password as string);

      setUserNameInUse(usernameValid === ValidUsernameResponseType.ALREADY_IN_USE);
      setUserNameFormatInvalid(usernameValid === ValidUsernameResponseType.IN_VALID_FORMAT);
      setPasswordValid(passwordValidResponse.isValid);

      if (usernameValid === ValidUsernameResponseType.NOT_IN_USE && passwordValidResponse.isValid) {
        // @ts-ignore
        navigation.navigate(SCREEN_NAMES.CONNECT);
      }
    }
  };

  const showTermsOfService = () => {
    // @ts-ignore
    navigation.navigate(SCREEN_NAMES.TERMS_OF_SERVICE);
  };

  const showContactUs = () => {
    resetForm();
    // @ts-ignore
    navigation.pop();
    // @ts-ignore
    navigation.navigate(SCREEN_NAMES.LAB_LOCATIONS);
  };

  const styles = StyleSheet.create({
    inputSpacing: {
      marginVertical: 5,
    },
    buttons: {
      minWidth: 250,
    },
    errorCard: {
      marginVertical: 5,
      borderWidth: 1,
      borderColor: theme.colors.errorContainer,
      backgroundColor: theme.colors.error,
    },
  });

  const PasswordErrorMessage = () => {
    return (
      <Card style={[styles.errorCard]}>
        <Card.Content>
          <HelperText type={'error'} style={{ color: theme.colors.onError }}>
            This password is used commonly. Please choose a different password.
          </HelperText>
        </Card.Content>
      </Card>
    );
  };

  const userNameError = useMemo(() => {
    return userNameInUse ? usernameInUseError() : userNameFormatInvalid ? usernameFormatError() : undefined;
  }, [userNameInUse]);

  return (
    <ScrollView style={{ backgroundColor: theme.colors.secondaryAccent }}>
      <Dialog style={{ marginVertical: 20 }}>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Header40>Why create an account?</Header40>
          <SubText style={{ marginVertical: 10 }}>
            Creating an account is required to successfully check-out and process your order. Once you've created an
            account, this will be the same information you use to order in the future and access your results.
          </SubText>
        </View>
        <AccountRegistration
          ref={registrationRef}
          firstName={firstName ?? ''}
          onFirstNameChanged={setFirstName}
          middleName={middleName}
          onMiddleNameChanged={setMiddleName}
          lastName={lastName ?? ''}
          onLastNameChanged={setLastName}
          dateOfBirth={dateOfBirth}
          onDateOfBirthChanged={setDateOfBirth}
          email={email}
          onEmailChanged={setEmail}
          username={userName}
          onUsernameChanged={setUserName}
          usernameValid={usernameIsValid}
          usernameError={userNameError}
          password={password ?? ''}
          onPasswordChanged={setPassword}
          isPasswordValid={passwordValid}
          passwordError={<PasswordErrorMessage />}
          usernameIsEmail={usernameIsEmail}
          onUsernameIsEmailChanged={onUsernameIsEmailChanged}
          confirmPassword={confirmPassword}
          onConfirmPasswordChanged={onConfirmPasswordChanged}
          signUpRelationshipEnabled={false}
          minorRegistrationEnabled={false}
        />
        <View>
          <LabeledSwitch
            style={styles.inputSpacing}
            value={agreeToTerms}
            onValueChanged={setAgreeToTerms}
            right={
              <Text style={{ marginHorizontal: 10 }}>
                I agree to the{' '}
                <Text onPress={showTermsOfService} style={{ textDecorationLine: 'underline' }}>
                  Terms of Service
                </Text>
              </Text>
            }
          />
          {!agreeToTerms && <HelperText type={'error'}>* You must agree to the terms of service.</HelperText>}
        </View>
        <View style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Button mode={'contained'} onPress={onCreateAccount} style={[styles.inputSpacing, styles.buttons]}>
            Create Account
          </Button>

          <Button mode={'contained-tonal'} onPress={onSignIn} style={[styles.inputSpacing, styles.buttons]}>
            Already have an account?
          </Button>
          <Button mode={'contained-tonal'} onPress={showContactUs} style={[styles.inputSpacing, styles.buttons]}>
            Need help?
          </Button>
        </View>
      </Dialog>
    </ScrollView>
  );
};
